import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axiosInstance';

const LIMIT = 10;

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [msg, setMsg] = useState('');
  const [exporting, setExporting] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async (p = 1) => {
    try {
      const res = await axiosInstance.get(`/auth/users?page=${p}&limit=${LIMIT}`);
      const data = res.data.users || [];
      setUsers(data);
      setTotalPages(res.data.pagination?.totalPages || 1);
      setTotalUsers(res.data.pagination?.totalUsers || data.length);
      setPage(p);
    } catch { setMsg('Failed to fetch users'); }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(users);
    } else {
      const q = search.toLowerCase();
      setFiltered(users.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.phone || '').includes(q)
      ));
    }
  }, [users, search]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await axiosInstance.get('/auth/users?page=1&limit=10000');
      const all = res.data.users || [];
      const hdrs = ['Name', 'Email', 'Phone', 'City', 'State', 'Country', 'Created At'];
      const rows = all.map(u => [u.name || '-', u.email || '-', u.phone || '-', u.address?.city || '-', u.address?.state || '-', u.address?.country || '-', new Date(u.createdAt).toLocaleDateString()]);
      const csv = [hdrs, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
      const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })), download: `users_${new Date().toISOString().slice(0, 10)}.csv` });
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch { setMsg('Export failed.'); }
    finally { setExporting(false); }
  };

  const pageNums = () => {
    const max = 5, start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + max - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const getAddressStr = u => {
    if (!u?.address) return '—';
    const a = u.address;
    return [a.street, a.city, a.state, a.country, a.pincode].filter(Boolean).join(', ') || '—';
  };

  return (
    <div className="users-page">
      <div className="ph">
        <div><h1>Users</h1><p>{totalUsers} total users</p></div>
        <div className="ph-actions">
          <button className="btn btn-outline btn-sm" onClick={exportCSV} disabled={exporting}>
            <i className="fas fa-download" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Toolbar Search */}
      <div className="toolbar">
        <div className="search-box">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            placeholder="Search user by name, email, phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="clear-btn" onClick={() => setSearch('')}>✕</button>}
        </div>
      </div>

      {msg && <div className="alert alert-error" style={{ marginBottom: 12 }}>{msg}<button className="alert-close" onClick={() => setMsg('')}>✕</button></div>}

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr><th>#</th><th>Avatar</th><th>Name</th><th>Email</th><th>Phone</th><th>City</th><th>Joined</th><th>Address</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="8"><div className="empty-state"><i className="fas fa-users" style={{ fontSize: 32, color: 'var(--t3)' }} /><p>No users found</p></div></td></tr>
            ) : filtered.map((u, idx) => (
              <tr key={u._id} onClick={() => setSelectedUser(u)} style={{ cursor: 'pointer' }}>
                <td className="col-num">{(page - 1) * LIMIT + idx + 1}</td>
                <td>
                  <img
                    src={u.profilePicture?.url || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || 'U')}&background=f97316&color=fff&size=32`}
                    alt="" className="user-avatar"
                  />
                </td>
                <td className="user-name-cell">{u.name || '—'}</td>
                <td className="user-email-cell">{u.email || '—'}</td>
                <td>{u.phone || '—'}</td>
                <td>{u.address?.city || '—'}</td>
                <td className="date-cell">{new Date(u.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                <td>
                  <span className="muted" style={{ fontSize: 11, maxWidth: 180, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getAddressStr(u)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {totalUsers > 0 && (
          <div className="pagi">
            <span className="pagi-info">Showing {users.length === 0 ? 0 : (page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, totalUsers)} of {totalUsers}</span>
            <div className="pagi-btns">
              <button className="pagi-btn" disabled={page === 1} onClick={() => fetchUsers(1)}>«</button>
              <button className="pagi-btn" disabled={page === 1} onClick={() => fetchUsers(page - 1)}>‹</button>
              {pageNums().map(p => <button key={p} className={`pagi-btn${p === page ? ' on' : ''}`} onClick={() => fetchUsers(p)}>{p}</button>)}
              <button className="pagi-btn" disabled={page === totalPages} onClick={() => fetchUsers(page + 1)}>›</button>
              <button className="pagi-btn" disabled={page === totalPages} onClick={() => fetchUsers(totalPages)}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* User details modal */}
      {selectedUser && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: 450, padding: 20, background: '#fff', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>User Profile Details</h3>
              <button className="clear-btn" onClick={() => setSelectedUser(null)} style={{ fontSize: 18 }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16 }}>
              <img
                src={selectedUser.profilePicture?.url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser.name || 'U')}&background=f97316&color=fff&size=64`}
                alt="" style={{ width: 54, height: 54, borderRadius: '50%', objectFit: 'cover' }}
              />
              <div>
                <h4 style={{ margin: 0, fontSize: 16 }}>{selectedUser.name || 'Anonymous User'}</h4>
                <div style={{ fontSize: 12, color: 'var(--tx2)' }}>ID: {selectedUser._id}</div>
              </div>
            </div>
            <div className="meta-card">
              <div className="meta-row"><span className="mk">Email</span><span className="mv">{selectedUser.email || '—'}</span></div>
              <div className="meta-row"><span className="mk">Phone</span><span className="mv">{selectedUser.phone || '—'}</span></div>
              <div className="meta-row"><span className="mk">Full Address</span><span className="mv">{getAddressStr(selectedUser)}</span></div>
              <div className="meta-row"><span className="mk">Registered</span><span className="mv">{new Date(selectedUser.createdAt).toLocaleString('en-IN')}</span></div>
            </div>
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button className="btn btn-primary btn-sm" onClick={() => setSelectedUser(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
